import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import { sortAlphabetically } from "../../utils/sortAlphabetically";
import { getUIDescription } from "./customerGroupDetails/AssigningCustomerGroupDevice";

const EditUserForm = ({ open, onClose, onSubmit, userData }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    parentGroup: "",
    webAccess: "no",
    loginPassword: "",
  });
  const [groups, setGroups] = useState([]);
  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingUser, setFetchingUser] = useState(false);
  const [freshUserData, setFreshUserData] = useState(null);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({
    phone: false,
  });

  useEffect(() => {
    if (open && userData?.id) {
      fetchUserDetails();
    }
  }, [open, userData?.id]);

  useEffect(() => {
    if (open) {
      fetchGroups();
      fetchApps();
    }
  }, [open]);
  const formatUsPhone = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length === 0) return "";
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6)
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6,
      10
    )}`;
  };

  // 📞 Validate phone (10 digits)
  const isValidUsPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, "");
    return cleaned.length === 10;
  };
  const handlePhoneChange = (e) => {
    const input = e.target.value;
    const cleaned = input.replace(/\D/g, "");

    if (cleaned.length <= 10) {
      const formatted = formatUsPhone(cleaned);
      setFormData((prev) => ({ ...prev, phone: formatted }));

      if (errors.phone && isValidUsPhone(formatted)) {
        setErrors((prev) => ({ ...prev, phone: false }));
      }
    }
  };

  const fetchUserDetails = async () => {
    if (!userData?.id) return;

    setFetchingUser(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/users/${userData.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const user = response.data;
      setFreshUserData(user);

      const groupId =
        userData.group?.id || user.group?.id || user.groupId || "";
      const formattedPhone = formatUsPhone(user.phone || "");
      setFormData({
        name: user.name || "",
        email: user.email || user.username || "",
        phone: formattedPhone,
        parentGroup: groupId,
        webAccess: user.appId || "no",
        loginPassword: user.password || "",
      });
    } catch (error) {
      console.error("Error fetching user details:", error);
      setError("Failed to load user details. Please try again.");

      // Fallback to passed userData if API call fails
      if (userData) {
        setFreshUserData(userData);
        setFormData({
          name: userData.name || "",
          email: userData.email || userData.username || "",
          phone: userData.phone || "",
          parentGroup: userData.group?.id || "",
          webAccess: "no",
          loginPassword: "",
        });
      }
    } finally {
      setFetchingUser(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/groups?type=geographical&companyId=${process.env.REACT_APP_COMPANY_ID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      //   const filtered = response.data.filter(item => {
      //     const name = item.name?.toLowerCase() || '';
      //     return name.includes('prc') || name.includes('root') || name.includes('precision');
      //   });
      //   setGroups(filtered);
      const sortedData = sortAlphabetically(response.data, "name");
      setGroups(sortedData);
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };
  const fetchApps = async () => {
    try {
      setLoadingApps(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/applications?companyId=${process.env.REACT_APP_COMPANY_ID}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApps(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching apps:", err);
      setApps([]);
    } finally {
      setLoadingApps(false);
    }
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const originalUser = {
        ...freshUserData,
        group: userData.group,
      };

      const updatePayload = {};
      const cleanedPhone = formData.phone.replace(/\D/g, "");
      if (cleanedPhone.length !== 10) {
        setErrors((prev) => ({ ...prev, phone: true }));
        return;
      }

      if (formData.name.trim() !== originalUser.name) {
        updatePayload.name = formData.name.trim();
      }
      if (
        formData.email.trim() !==
        (originalUser.email || originalUser.username || "")
      ) {
        updatePayload.email = formData.email.trim();
      }
      if (cleanedPhone !== (originalUser.phone || "")) {
        updatePayload.phone = cleanedPhone;
      }
      const currentGroupId = originalUser.group?.id || "";

      if (formData.parentGroup !== currentGroupId) {
        updatePayload.groupId = formData.parentGroup;
      }
      if (formData.loginPassword.trim() !== originalUser.password) {
        updatePayload.password = formData.loginPassword.trim();
      }

      // if (formData.webAccess !== (originalUser.webAccess || 'no')) {
      //   updatePayload.webAccess = formData.webAccess;
      // }

      const currentAppId = originalUser.appId || "no";
      if (formData.webAccess !== currentAppId) {
        updatePayload.appId =
          formData.webAccess === "no" ? null : formData.webAccess;
      }
      if (Object.keys(updatePayload).length === 0) {
        toast.success("No changes were made");
        setLoading(false);
        onClose();
        return;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_API_GATEAWAY}/users/${userData.id}`,
        updatePayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("User updated successfully:", response.data);

      onSubmit({
        ...originalUser,
        ...updatePayload,
        id: userData.id,
      });
      toast.success("✅ User updated successfully!");

      onClose();
    } catch (error) {
      const rawMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data ||
        error.message;

      let errorMessage = "Failed to create user";

      if (typeof rawMessage === "string") {
        const lowerMsg = rawMessage.toLowerCase();

        if (lowerMsg.includes("email")) {
          errorMessage = "This email address is already in use.";
        } else if (
          lowerMsg.includes("username") &&
          lowerMsg.includes("already exists with")
        ) {
          errorMessage = "This username is already taken.";
        } else if (
          lowerMsg.includes("phone") &&
          lowerMsg.includes("already exists with")
        ) {
          errorMessage = "This phone number is already in use.";
        } else if (lowerMsg.includes("phone")) {
          errorMessage = "Please enter a valid 10-digit phone number.";
        } else if (rawMessage.includes("UserService update failed:")) {
          errorMessage = rawMessage
            .split("UserService update failed:")[1]
            .trim();
        } else {
          errorMessage = rawMessage;
        }
      }

      console.error("Error updating user:", rawMessage);
      setError(errorMessage || "Failed to update user. Please try again.");
      toast.error(error.response?.data?.message || "❌ Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  const handleTestSMS = async () => {
    try {
      // Implement SMS test functionality here
      console.log("Testing SMS for user:", userData.phone);
      const payload = {
        phoneList: [userData.phone],
        body: `TEST FROM OPENAIR at ${new Date().toLocaleString()}`,
        // unitId: null,
        groupName: "TEST",
        UI_Description: `${getUIDescription()}\n Test SMS`, 
      };
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/alarms/sms/send-sms`,
        payload
      );
      toast.success(`Sending Test SMS To ${userData.phone}`);
    } catch (error) {
      console.error("Error testing SMS:", error);
      toast.error("Failed to send test SMS");
    }
  };
  const handleClose = () => {
    setError("");
    setFormData({
      name: "",
      email: "",
      phone: "",
      parentGroup: "",
      webAccess: "no",
      loginPassword: "",
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Edit user
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {fetchingUser ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress color="#60a5fa" />
            <Typography sx={{ ml: 2 }}>Loading user details...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {/* Name Field */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
            >
              <Typography sx={{ minWidth: 120, fontWeight: "bold" }}>
                Name
              </Typography>
              <TextField
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={loading}
              />
            </Box>

            {/* Parent Group Field */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
            >
              <Typography sx={{ minWidth: 120, fontWeight: "bold" }}>
                Parent Group
              </Typography>
              <FormControl fullWidth size="small" disabled={loading}>
                <InputLabel>Select Group</InputLabel>
                <Select
                  name="parentGroup"
                  value={formData.parentGroup}
                  onChange={handleChange}
                  label="Select Group"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Email Address Field */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
            >
              <Typography sx={{ minWidth: 120, fontWeight: "bold" }}>
                Email Address
              </Typography>
              <TextField
                name="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                size="small"
                type="email"
                disabled={true}
              />
            </Box>

            {/* Web Access Field */}
            {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ minWidth: 120, fontWeight: "bold" }}>Web Access</Typography>
              <FormControl fullWidth size="small" disabled={loading}>
                <InputLabel>Web Access</InputLabel>
                <Select
                  name="webAccess"
                  value={formData.webAccess}
                  onChange={handleChange}
                  label="Web Access"
                >
                  <MenuItem value="no">--- No Web Access ---</MenuItem>
                </Select>
              </FormControl>
            </Box> */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
            >
              <Typography sx={{ minWidth: 120, fontWeight: "bold" }}>
                Web Access
              </Typography>
              <FormControl fullWidth size="small" disabled={loading}>
                <InputLabel>Web Access</InputLabel>
                <Select
                  name="webAccess"
                  value={formData.webAccess}
                  onChange={handleChange}
                  label="Web Access"
                >
                  <MenuItem value="no">No Web Access</MenuItem>
                  {Array.isArray(apps) && apps.length > 0 ? (
                    apps.map((app) => (
                      <MenuItem key={app.id} value={app.id}>
                        {app.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      {loadingApps ? "Loading apps..." : "No apps available"}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            {/* Phone Field */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
            >
              <Typography sx={{ minWidth: 120, fontWeight: "bold" }}>
                Phone
              </Typography>
              <TextField
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                fullWidth
                size="small"
                error={errors.phone}
                disabled={loading}
                helperText={
                  errors.phone
                    ? formData.phone.trim() === ""
                      ? "Phone number is required"
                      : "Enter a valid 10-digit phone number"
                    : "Format: (123) 456-7890"
                }
              />
            </Box>

            {/* Test SMS Chip */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ minWidth: 120 }}></Typography>
              <Chip
                label="Test SMS"
                variant="outlined"
                color="primary"
                onClick={handleTestSMS}
                disabled={loading}
              />
            </Box>

            {/* Login Password Field */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
            >
              <Typography sx={{ minWidth: 120, fontWeight: "bold" }}>
                Login Password
              </Typography>
              <TextField
                name="loginPassword"
                value={formData.loginPassword}
                onChange={handleChange}
                fullWidth
                size="small"
                type="password"
                placeholder="Enter new password"
                disabled={loading}
                helperText="Leave empty to keep current password"
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          sx={{
            borderRadius: 0, // ✅ remove rounding
          }}
          onClick={handleClose}
          color="error"
          disabled={loading || fetchingUser}
        >
          Cancel
        </Button>
        <Button
          sx={{
            borderRadius: 0, // ✅ remove rounding
          }}
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || fetchingUser}
          color="primary"
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserForm;
